import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { LanguageService } from '../../services';

type LocalizedCopy = {
  bn: string;
  en: string;
};

type IconMarkup = string | SafeHtml;

@Component({
  selector: 'app-auth-showcase',
  standalone: true,
  templateUrl: './auth-showcase.component.html',
  styleUrl: './auth-showcase.component.css'
})
export class AuthShowcaseComponent implements AfterViewInit, OnDestroy {
  private readonly featureGridTransitionMs = 0;
  private featureVisibilityFrame: number | null = null;
  private featuresResizeObserver: ResizeObserver | null = null;
  private featureGridTransitionTimeout: ReturnType<typeof setTimeout> | null = null;
  private deferredFeatureVisibilityTimeouts: Array<ReturnType<typeof setTimeout>> = [];
  private featureGridColumns = 1;
  private featureGridRowGap = 0;
  private featureGridTileHeight = 72;
  private isFeatureGridTransitioning = false;

  @ViewChild('featuresCard') private featuresCardRef?: ElementRef<HTMLElement>;
  @ViewChild('featuresHeading') private featuresHeadingRef?: ElementRef<HTMLElement>;
  @ViewChild('featureGrid') private featureGridRef?: ElementRef<HTMLElement>;

  readonly showcaseDescription: LocalizedCopy = {
    bn: 'ফ্ল্যাট ম্যানেজমেন্টের সকল কাজকর্ম এক প্ল্যাটফর্মে সহজ, স্বচ্ছ ও নিখুঁতভাবে পরিচালনা করুন',
    en: 'Manage all flat operations from one platform with a simple, transparent, and organized workflow'
  };

  readonly featureHeading: LocalizedCopy = {
    bn: 'আমাদের ফ্ল্যাট ম্যানেজমেন্ট সফটওয়্যারটিতে যা যা থাকছে:',
    en: 'Included in our flat management software:'
  };

  isFeaturesExpanded = false;
  renderExpandedFeatures = false;
  collapsedFeatureCount = 6;
  featureGridMaxHeight: number | null = null;

  readonly features: Array<{ icon: IconMarkup; title: LocalizedCopy }> = [
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 2L4 9v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-8-7zm0 13a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/></svg>`,
      title: { bn: 'বাড়ি পরিচালনা', en: 'House Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5zm12 4H9v2h6V9zm0 4H9v2h6v-2z"/></svg>`,
      title: { bn: 'ফ্ল্যাট পরিচালনা', en: 'Flat Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M8 11a3 3 0 1 0 0-6a3 3 0 0 0 0 6Z"/><path d="M16.5 10a2.5 2.5 0 1 0 0-5a2.5 2.5 0 0 0 0 5Z"/><path d="M3.5 18.5c.8-2 2.9-3.3 5.5-3.3s4.7 1.3 5.5 3.3"/><path d="M13 17.8c.7-1.5 2.3-2.5 4.3-2.5c1.7 0 3.2.7 4.2 2"/></svg>`,
      title: { bn: 'ভাড়াটিয়া পরিচালনা', en: 'Renter Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm-7 14h-2v-2h2v2zm3.5-4h-5v-2h5v2zm0-4h-5V7h5v2z"/></svg>`,
      title: { bn: 'ভাড়া ও খরচ ট্র্যাকিং', en: 'Rent & Expense Tracking' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>`,
      title: { bn: 'স্মার্ট ড্যাশবোর্ড', en: 'Smart Dashboard' }
    },
    {
      icon: `<svg width="22" height="20" viewBox="0 0 22 20" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.54248C1 2.586 2.586 1 4.54248 1H17.4575C19.414 1 21 2.586 21 4.54248V15.4575C21 17.414 19.414 19 17.4575 19H4.54248C2.586 19 1 17.414 1 15.4575V4.54248ZM4.54248 3.51863H17.4575C18.0229 3.51863 18.4814 3.97708 18.4814 4.54248V5.00643H3.51863V4.54248C3.51863 3.97708 3.97708 3.51863 4.54248 3.51863ZM18.4814 7.52494H3.51863V15.4575C3.51863 16.0229 3.97708 16.4814 4.54248 16.4814H17.4575C18.0229 16.4814 18.4814 16.0229 18.4814 15.4575V7.52494ZM6.061 10.0434H11.5367V11.3323H6.061V10.0434ZM15.939 10.0434H13.4722V11.3323H15.939V10.0434ZM6.061 12.6212H15.939V13.9101H6.061V12.6212Z" /></svg>`,
      title: { bn: 'রশিদ পরিচালনা', en: 'Receipt Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>`,
      title: { bn: 'আর্থিক সারসংক্ষেপ', en: 'Financial Summary' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.8c-2.95 3.3-5.2 5.98-5.2 9.03A5.2 5.2 0 0 0 12 17.03a5.2 5.2 0 0 0 5.2-5.2c0-3.05-2.25-5.73-5.2-9.03Zm1.05 8.36h-1.47l.58-2.07c.05-.18-.15-.31-.29-.18l-2.38 2.27h1.47l-.58 2.07c-.05.18.15.31.29.18l2.38-2.27ZM8.9 20.5h6.2V19h-6.2v1.5Z"/></svg>`,
      title: { bn: 'ইউটিলিটি বিল পরিচালনা', en: 'Utility Bill Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M21.1 7.2l-4.3-4.3a1.2 1.2 0 0 0-1.7 0l-1.6 1.6l1.8 1.8l-1.4 1.4l-3-3a1.2 1.2 0 0 0-1.7 0L8 5.8c-.47.47-.47 1.24 0 1.7l1.5 1.5l-5.8 5.8a2.2 2.2 0 0 0 0 3.1l1.4 1.4a2.2 2.2 0 0 0 3.1 0l5.8-5.8l1.5 1.5c.47.47 1.24.47 1.7 0l1.1-1.1a1.2 1.2 0 0 0 0-1.7l-3-3l1.4-1.4l1.8 1.8l1.6-1.6c.47-.47.47-1.24 0-1.7Z"/></svg>`,
      title: { bn: 'রক্ষণাবেক্ষণ খরচ ট্র্যাকিং', en: 'Maintenance Cost Tracking' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M18.9 6.5H5.1l-1.3 4h17.4l-2.3-4zM4.1 12v6c0 1.1.9 2 2 2h11.8c1.1 0 2-.9 2-2v-6H4.1zm5.9 6H7.9v-4h2.1v4zm6.1 0h-2.1v-4h2.1v4z"/></svg>`,
      title: { bn: 'গ্যারেজ পরিচালনা', en: 'Garage Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-5h2v5zm4 0h-2v-3h2v3zm4 0h-2v-5h2v5z"/></svg>`,
      title: { bn: 'রিপোর্টিং সিস্টেম', en: 'Reporting System' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 12c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0 2c-2.7 0-8 1.3-8 4v2h16v-2c0-2.7-5.3-4-8-4z"/></svg>`,
      title: { bn: 'কর্মচারী পরিচালনা (শীঘ্রই)', en: 'Employee Management (Coming Soon)' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7v-5z"/></svg>`,
      title: { bn: 'বিল নোটিশ সিস্টেম (শীঘ্রই)', en: 'Bill Notice System (Coming Soon)' }
    }
  ];

  constructor(
    private readonly language: LanguageService,
    private readonly sanitizer: DomSanitizer
  ) {
    this.features.forEach((feature) => {
      feature.icon = this.sanitizer.bypassSecurityTrustHtml(`${feature.icon}`);
    });
    this.updateFeatureVisibility();
  }

  ngAfterViewInit(): void {
    this.scheduleFeatureVisibilityUpdate();
    this.scheduleDeferredFeatureVisibilityUpdates();

    if (typeof ResizeObserver === 'undefined' || !this.featuresCardRef?.nativeElement) {
      return;
    }

    this.featuresResizeObserver = new ResizeObserver(() => this.scheduleFeatureVisibilityUpdate());
    this.featuresResizeObserver.observe(this.featuresCardRef.nativeElement);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleFeatureVisibilityUpdate();
  }

  ngOnDestroy(): void {
    this.featuresResizeObserver?.disconnect();
    if (this.featureVisibilityFrame !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(this.featureVisibilityFrame);
    }
    if (this.featureGridTransitionTimeout !== null) {
      clearTimeout(this.featureGridTransitionTimeout);
    }
    this.deferredFeatureVisibilityTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
  }

  localize(copy: LocalizedCopy): string {
    return this.language.currentLanguage === 'bn' ? copy.bn : copy.en;
  }

  toggleFeatures() {
    if (this.isFeatureGridTransitioning) {
      return;
    }

    const grid = this.featureGridRef?.nativeElement;
    const currentHeight = Math.ceil(this.featureGridRef?.nativeElement.getBoundingClientRect().height ?? 0);
    if (currentHeight > 0) {
      this.featureGridMaxHeight = currentHeight;
    }

    if (!grid || typeof window === 'undefined') {
      this.renderExpandedFeatures = !this.renderExpandedFeatures;
      this.isFeaturesExpanded = this.renderExpandedFeatures;
      this.scheduleFeatureVisibilityUpdate(true);
      return;
    }

    this.isFeatureGridTransitioning = true;

    if (this.isFeaturesExpanded) {
      const collapsedHeight = this.getCollapsedGridHeight();
      window.requestAnimationFrame(() => {
        this.featureGridMaxHeight = collapsedHeight;
      });

      this.featureGridTransitionTimeout = setTimeout(() => {
        this.featureGridTransitionTimeout = null;
        this.renderExpandedFeatures = false;
        this.isFeaturesExpanded = false;
        this.isFeatureGridTransitioning = false;
        this.scheduleFeatureVisibilityUpdate();
      }, this.featureGridTransitionMs);
      return;
    }

    this.renderExpandedFeatures = true;
    this.isFeaturesExpanded = true;
    window.requestAnimationFrame(() => {
      const expandedHeight = Math.ceil(grid.scrollHeight || grid.getBoundingClientRect().height || 0);
      this.featureGridMaxHeight = expandedHeight > 0 ? expandedHeight : null;
    });

    this.featureGridTransitionTimeout = setTimeout(() => {
      this.featureGridTransitionTimeout = null;
      this.isFeatureGridTransitioning = false;
      this.applyFeatureGridHeight(false);
    }, this.featureGridTransitionMs);
  }

  get visibleFeatures() {
    return this.renderExpandedFeatures ? this.features : this.features.slice(0, this.collapsedFeatureCount);
  }

  get shouldShowFeatureToggle(): boolean {
    return this.features.length > this.collapsedFeatureCount;
  }

  private scheduleFeatureVisibilityUpdate(animate = false): void {
    if (typeof window === 'undefined') {
      this.updateFeatureVisibility(animate);
      return;
    }

    if (this.featureVisibilityFrame !== null) {
      window.cancelAnimationFrame(this.featureVisibilityFrame);
    }

    this.featureVisibilityFrame = window.requestAnimationFrame(() => {
      this.featureVisibilityFrame = null;
      this.updateFeatureVisibility(animate);
    });
  }

  private scheduleDeferredFeatureVisibilityUpdates(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.deferredFeatureVisibilityTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.deferredFeatureVisibilityTimeouts = [
      setTimeout(() => this.scheduleFeatureVisibilityUpdate(), 0),
      setTimeout(() => this.scheduleFeatureVisibilityUpdate(), 180),
      setTimeout(() => this.scheduleFeatureVisibilityUpdate(), 420)
    ];
  }

  private updateFeatureVisibility(animate = false): void {
    if (typeof window === 'undefined') {
      this.collapsedFeatureCount = Math.min(this.features.length, 6);
      return;
    }

    const featureGrid = this.featureGridRef?.nativeElement;
    const featuresCard = this.featuresCardRef?.nativeElement;
    const featuresHeading = this.featuresHeadingRef?.nativeElement;

    const isMobileWidth = window.innerWidth <= 640;
    let columns = isMobileWidth ? 1 : window.innerWidth <= 1180 ? 2 : 3;
    let rows = window.innerHeight <= 760 ? 2 : window.innerHeight <= 980 ? 3 : 4;
    const minimumRows = isMobileWidth ? 3 : 1;

    if (featureGrid && featuresCard && featuresHeading) {
      const gridStyle = window.getComputedStyle(featureGrid);
      const cardStyle = window.getComputedStyle(featuresCard);
      const headingStyle = window.getComputedStyle(featuresHeading);
      const sampleFeature = featureGrid.querySelector<HTMLElement>('.auth-showcase__feature');
      const sampleButton = featureGrid.querySelector<HTMLElement>('.auth-showcase__more-btn--tile');
      const trackList = gridStyle.gridTemplateColumns.split(' ').filter(Boolean);
      const measuredColumns = trackList.length;
      const rowGap = Number.parseFloat(gridStyle.rowGap || '0') || 0;
      const cardPaddingTop = Number.parseFloat(cardStyle.paddingTop || '0') || 0;
      const cardPaddingBottom = Number.parseFloat(cardStyle.paddingBottom || '0') || 0;
      const headingMarginBottom = Number.parseFloat(headingStyle.marginBottom || '0') || 0;
      const tileHeight = Math.max(
        sampleFeature?.getBoundingClientRect().height ?? 0,
        sampleButton?.getBoundingClientRect().height ?? 0,
        window.innerWidth <= 640 ? 76 : 72
      );
      const availableGridHeight = featuresCard.clientHeight - cardPaddingTop - cardPaddingBottom - featuresHeading.clientHeight - headingMarginBottom;

      if (measuredColumns > 0) {
        columns = measuredColumns;
      }

      if (availableGridHeight > 0 && tileHeight > 0) {
        rows = Math.max(minimumRows, Math.floor((availableGridHeight + rowGap) / (tileHeight + rowGap)));
      }

      this.featureGridRowGap = rowGap;
      this.featureGridTileHeight = tileHeight;
    }

    rows = Math.max(rows, minimumRows);

    this.featureGridColumns = Math.max(columns, 1);
    this.featureGridRowGap = Math.max(this.featureGridRowGap, 0);
    this.featureGridTileHeight = Math.max(this.featureGridTileHeight, 1);

    const totalSlots = Math.max(columns * rows, columns === 1 ? 2 : columns);
    const needsToggle = this.features.length > totalSlots;

    this.collapsedFeatureCount = Math.min(this.features.length, needsToggle ? Math.max(totalSlots - 1, 1) : totalSlots);
    if (!this.isFeatureGridTransitioning) {
      this.applyFeatureGridHeight(animate);
    }
  }

  private applyFeatureGridHeight(animate: boolean): void {
    if (typeof window === 'undefined') {
      return;
    }

    const grid = this.featureGridRef?.nativeElement;
    if (!grid) {
      return;
    }

    if (this.featureGridTransitionTimeout !== null) {
      clearTimeout(this.featureGridTransitionTimeout);
      this.featureGridTransitionTimeout = null;
    }

    const setTargetHeight = () => {
      const targetHeight = Math.ceil(grid.scrollHeight || grid.getBoundingClientRect().height || 0);
      this.featureGridMaxHeight = targetHeight > 0 ? targetHeight : null;
    };

    if (!animate) {
      setTargetHeight();
      return;
    }

    window.requestAnimationFrame(() => {
      setTargetHeight();
      this.featureGridTransitionTimeout = setTimeout(() => {
        this.featureGridTransitionTimeout = null;
        setTargetHeight();
      }, this.featureGridTransitionMs);
    });
  }

  private getCollapsedGridHeight(): number | null {
    const columns = Math.max(this.featureGridColumns, 1);
    const itemsInCollapsedView = this.collapsedFeatureCount + (this.features.length > this.collapsedFeatureCount ? 1 : 0);
    const rows = Math.max(1, Math.ceil(itemsInCollapsedView / columns));
    const collapsedHeight = rows * this.featureGridTileHeight + Math.max(0, rows - 1) * this.featureGridRowGap;
    return collapsedHeight > 0 ? Math.ceil(collapsedHeight) : null;
  }
}
